import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BaseService} from '../base-service';
import {NetworkProvider, NetworkProviderType} from '../access-sites.interface';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {PagedModel} from '@shared/api/model/paged-model';
import {ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot} from '@angular/router';
import {APIResponse} from '@app/layout/admin/services/api/api.interface';
import {GQLPagingParamsVariableWithoutNoCount, GRAPHQL_TYPES, GraphQlVariables, HttpUtilityService} from '@shared/services/api/http-utility-service';
import {StringUtils} from '@shared/util/general/string-utils';
import Fields from "gql-query-builder/build/Fields";
import * as gql from 'gql-query-builder';
import {CollectionUtils} from "@shared/util/collections/collection-utils";

enum NETWORK_PROVIDER_OPERATIONS {
  NETWORK_PROVIDER_TYPE = 'network_provider_type',
  NETWORK_PROVIDER_TYPES = 'network_provider_types',
  NETWORK_PROVIDER = 'network_provider',
  NETWORK_PROVIDERS = 'network_providers'
}

interface ProviderAPIDelete {
  network_provider_id: number;
  deleted: boolean;
}

export const networkProvidersResolver: ResolveFn<NetworkProvider> = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<NetworkProvider> => {
  return inject(NetworkProvidersService).getNetworkProvider(StringUtils.stringToNumber(route.paramMap.get('id')));
};

export type NetworkProviderId = number;
export type NetworkProviderTypeId = number;

export interface NetworkProvidersSearchTerm {
  network_provider_id?: NetworkProviderId;
  name?: string;
  network_provider_type_id?: NetworkProviderTypeId;
  shared?: boolean;
}

const networkProviderFields: Fields = [
  'network_provider_id',
  'name',
  'shared',
  'customer_id',
  'network_provider_type_id',
  {
    network_provider_type: [
      'network_provider_type_id',
      'network_provider_type_name'
    ]
  }
];

const networkProviderGraphqlVariables: GraphQlVariables = {
  ...GQLPagingParamsVariableWithoutNoCount,
  network_provider_id: {type: GRAPHQL_TYPES.INTEGER},
  name: {type: GRAPHQL_TYPES.STRING},
  network_provider_type_id: {type: GRAPHQL_TYPES.INTEGER},
  shared: {type: GRAPHQL_TYPES.BOOLEAN}
};


@Injectable({
  providedIn: 'root'
})
export class NetworkProvidersService extends BaseService {

  private readonly PROVIDER_URL_PART = '/network-providers';
  private readonly PROVIDER_TYPES_URL_PART = '/' + this.PROVIDER_URL_PART + '/types';
  private readonly logger: AceLogger;

  constructor(private http: HttpClient,
              private httpUtilityService: HttpUtilityService,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(NetworkProvidersService.name);
  }

  public getNetworkProviderTypes(): Observable<NetworkProviderType[]> {
    const options = {headers: this.getHeaders()};
    return this.http.get(this.getGateway() + this.PROVIDER_TYPES_URL_PART, options)
      .pipe(map((response: APIResponse<NetworkProviderType[]>) => this.httpUtilityService.extractResponseData(response, NETWORK_PROVIDER_OPERATIONS.NETWORK_PROVIDER_TYPES)));
  }

  public getNetworkProviders(searchTerm: NetworkProvidersSearchTerm | null, startIndex: number, limit: number): Observable<PagedModel<NetworkProvider[]>> {
    const operation = NETWORK_PROVIDER_OPERATIONS.NETWORK_PROVIDERS.valueOf();
    const q = gql.query({
      operation,
      fields: networkProviderFields,
      variables: networkProviderGraphqlVariables
    });
    return this.httpUtilityService.graphQL(q, operation, {...searchTerm, start_index: startIndex, limit});
  }

  public getNetworkProvider(id: number): Observable<NetworkProvider> {
    return this.getNetworkProviders({network_provider_id: id}, 0, 1).pipe(map(networkProviders => CollectionUtils.only(networkProviders.data)));
  }

  public createNetworkProvider(networkProvider: NetworkProvider): Observable<NetworkProvider> {
    const np = this.prepareObjectForApi(networkProvider);
    const options = {headers: this.getHeaders()};
    return this.http.post(this.getGateway() + this.PROVIDER_URL_PART, np, options)
      .pipe(map((response: APIResponse<NetworkProvider>) => this.httpUtilityService.extractResponseData(response, NETWORK_PROVIDER_OPERATIONS.NETWORK_PROVIDER)));
  }

  public updateNetworkProvider(networkProvider: NetworkProvider): Observable<NetworkProvider> {
    const np = this.prepareObjectForApi(networkProvider);
    const options = {headers: this.getHeaders()};
    return this.http.put(this.getGateway() + this.PROVIDER_URL_PART + '/' + np.network_provider_id, np, options)
      .pipe(map((response: APIResponse<NetworkProvider>) => this.httpUtilityService.extractResponseData(response, NETWORK_PROVIDER_OPERATIONS.NETWORK_PROVIDER)));
  }

  public changeSharing(networkProvider: NetworkProvider): Observable<NetworkProvider> {
    networkProvider.shared = !networkProvider.shared;
    return this.updateNetworkProvider(networkProvider);
  }

  public deleteNetworkProvider(id: number): Observable<number> {
    const options = {headers: this.getHeaders()};
    return this.http.delete(this.getGateway() + this.PROVIDER_URL_PART + '/' + id, options)
      .pipe(map((response: APIResponse<ProviderAPIDelete>) => {
        return this.httpUtilityService.extractResponseData<ProviderAPIDelete>(response, NETWORK_PROVIDER_OPERATIONS.NETWORK_PROVIDER).network_provider_id;
      }));
  }

  private prepareObjectForApi(networkProvider: NetworkProvider): NetworkProvider {
    const np = Object.assign({}, networkProvider);
    delete np.network_provider_type;
    return np;
  }
}
